import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LockClosedIcon, EyeIcon, EyeSlashIcon, ScaleIcon } from '../icons';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { getPostLoginPath } from '../config/auth';

const ChangePassword = () => {
  const { user, persistSessionFromAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (form.current_password === form.new_password) {
      setError('New password must be different from the temporary password.');
      return;
    }

    setLoading(true);
    try {
      const resp = await authAPI.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      const envelope = resp.data?.data || {};
      const apiUser = envelope.user;
      const token = envelope.token;
      if (!apiUser || !token) {
        setError('Password updated but session refresh failed. Please sign in again.');
        return;
      }

      const userData = {
        id: apiUser.user_id || apiUser.id,
        email: apiUser.email,
        first_name: apiUser.first_name || '',
        last_name: apiUser.last_name || '',
        is_active: apiUser.is_active !== false,
        phone_number: apiUser.phone,
        role: String(apiUser.role || 'registrar').toLowerCase().replace(/\s+/g, '_'),
        court_name: apiUser.court_name || '',
        tenant_id: apiUser.tenant_id || null,
        must_change_password: false,
      };

      if (persistSessionFromAuth) {
        persistSessionFromAuth(userData, token);
      }
      toast.success('Password updated');
      navigate(getPostLoginPath(userData.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'appearance-none relative block w-full pl-10 pr-10 py-3 border border-legal-300 placeholder-legal-400 text-legal-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500';

  return (
    <div className="min-h-screen gradient-legal flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <div className="flex justify-center">
            <img src="/logo.png" alt="CourtScribe" className="h-14 w-14 rounded-2xl shadow-lg" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-legal-900">Update your password</h1>
          <p className="mt-2 text-sm text-legal-600">
            You signed in with a temporary password{user?.email ? ` for ${user.email}` : ''}.
            Choose a new password to continue.
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-legal-200">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {[
              { key: 'current_password', label: 'Temporary / current password', showKey: 'current' },
              { key: 'new_password', label: 'New password', showKey: 'next' },
              { key: 'confirm_password', label: 'Confirm new password', showKey: 'confirm' },
            ].map(({ key, label, showKey }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-legal-700 mb-2">{label}</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-legal-400" />
                  <input
                    type={show[showKey] ? 'text' : 'password'}
                    required
                    value={form[key]}
                    onChange={(e) => onChange(key, e.target.value)}
                    className={inputCls}
                    autoComplete={key === 'current_password' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => ({ ...s, [showKey]: !s[showKey] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-legal-400 hover:text-legal-600"
                  >
                    {show[showKey] ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            ))}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-white gradient-court disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-2">
                <ScaleIcon className="h-5 w-5" />
                {loading ? 'Updating…' : 'Save new password'}
              </span>
            </button>
          </form>

          <button
            type="button"
            onClick={logout}
            className="mt-4 w-full text-sm text-legal-500 hover:text-legal-800"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
